// src/utils/fallbackConfig.ts
import type {
    RestaurantConfig,
    ColorConfig,
    FontsConfig,
} from "../ui/uiConfig";

export const defaultColorConfig: ColorConfig = {
    defaultTheme: "light",
    themes: {
        light: {
            primary: "#ffffff",
            secondary: "#343434",
            brand1: "#CC6828",
            brand2: "#00774D",
            brand3: "#00774D",
            brand4: "fff",
            brand5: "000"
        },
        dark: {
            primary: "#343434",
            secondary: "#ffffff",
            brand1: "#FF8C42",
            brand2: "#00A36F",
            brand3: "#00A36F",
            brand4: "fff",
            brand5: "000"
        },
    },
};

export const defaultFontConfigV2: FontsConfig = {
    primary: { name: "Poppins, sans-serif", url: "" },
    secondary: { name: "Poppins, sans-serif", url: "" },
    tertiary: { name: "Poppins, sans-serif", url: "" },

    sizePrimary: 16,
    sizeSecondary: 14,
    sizeTertiary: 12,
    sizeXS: 10,
};

export const fallbackConfig: RestaurantConfig = {
    // Runtime / metadata
    id: "",
    title: "Cargando...",
    logo: "",
    favicon: "",
    phoneNumber: "",
    placeholder_url: null,
    card_info: null,
    header_image_url: null,
    address: null,
    opening_hours: null,

    // DB colors (normalizado luego; fallback por ahora)
    colors: defaultColorConfig,

    // Schema v2
    version: "2.1",
    schema: "menugraphic.ui.v2",

    fonts: defaultFontConfigV2,

    tokens: {
        radius: { sm: 8, md: 12, lg: 16, full: 9999 },
        space: { xs: 8, sm: 12, md: 16, lg: 24, xl: 32 },
        shadow: {
            sm: "0 2px 10px rgba(0,0,0,0.06)",
            md: "0 6px 18px rgba(0,0,0,0.12)",
            lg: "0 10px 30px rgba(0,0,0,0.18)",
        },
    },

    ui: {
        card: { borderRadius: 12, shadow: "shadow.sm" },
        button: { borderRadius: 10, paddingX: "14px", paddingY: "10px", fontSize: "sizeSecondary" },
        input: {
            borderRadius: 10,
            padding: "10px 12px",
            backgroundColor: "primary",
            textColor: "secondary",
            placeholderColor: "secondary",
            borderColor: "transparent",
            focusBorderColor: "brand1",
        },
        modal: { padding: 12, shadow: "shadow.lg", overlayColor: "rgba(0,0,0,0.45)" },
    },

    components: {
        navbar: {
            cssPrefix: "nav",
            heightMobile: 160,
            heightDesktop: 200,
            styles: {
                backgroundColor: "primary",
                topBackgroundColor: "primary",
                padding: "space.sm",
                gap: "space.xs",
                borderRadius: 0,
                shadow: "shadow.sm",
                topBarHeight: 90,
                logoSize: 80,
                logoBackgroundColor: "transparent",
                logoBorderRadius: 8,

                searchBackgroundColor: "primary",
                searchTextColor: "secondary",
                searchPlaceholderColor: "secondary",
                searchIconColor: "secondary",
                searchIconBackgroundColor: "primary",
                searchBorderRadius: 10,

                iconBackgroundColor: "brand1",
                iconColor: "secondary",
                iconBorderRadius: 8,

                linkColor: "secondary",
                linkHoverColor: "brand2",
            },
        },

        carouselCategories: {
            cssPrefix: "carousel-categories",
            styles: {
                backgroundColor: "primary",
                padding: "space.sm",
                gap: "space.xs",
                borderRadius: "radius.md",

                itemBackgroundColor: "primary",
                itemTextColor: "secondary",
                itemBorderRadius: 10,

                itemSelectedBackgroundColor: "brand2",
                itemSelectedTextColor: "primary",

                itemHoverBackgroundColor: "brand1",
                itemHoverTextColor: "primary",

                fontSize: "sizeXS",
            },
        },

        carouselContent: {
            cssPrefix: "carousel-content",
            styles: {
                backgroundColor: "primary",
                padding: "space.sm",
                borderRadius: "radius.md",
                shadow: "shadow.sm",
                paginationActiveColor: "brand1",
                paginationInactiveColor: "secondary",
            },
        },

        mainPlates: {
            cssPrefix: "featured-plates",
            styles: {
                backgroundColor: "primary",
                headerBackgroundColor: "primary",
                headerTextColor: "secondary",
                headerFontSize: "sizePrimary",

                cardBackgroundColor: "primary",
                cardBorderRadius: "radius.md",
                cardShadow: "shadow.sm",
                cardGap: 10,

                titleColor: "secondary",
                titleFontSize: "sizeSecondary",
                descriptionColor: "secondary",
                descriptionFontSize: "sizeTertiary",
                priceColor: "brand2",
                priceFontSize: "sizeSecondary",
            },
        },

        menuPlates: {
            cssPrefix: "menu-plates",
            styles: {
                backgroundColor: "primary",
                categoryBackgroundColor: "primary",
                categoryTextColor: "secondary",
                categoryFontSize: "sizePrimary",
                subcategoryBackgroundColor: "primary",
                subcategoryTextColor: "brand2",
                subcategoryFontSize: "sizeSecondary",
                cardBackgroundColor: "primary",
                cardBorderRadius: "radius.md",
                cardShadow: "shadow.sm",
                cardGap: 10,
            },
        },

        plateCard: {
            cssPrefix: "plate-card",
            styles: {
                backgroundColor: "primary",
                borderRadius: "radius.md",
                padding: "space.sm",
                titleColor: "secondary",
                titleFontSize: "sizeSecondary",
                priceColor: "brand2",
                priceFontSize: "sizeSecondary",
                descriptionColor: "secondary",
                descriptionFontSize: "sizeTertiary",
                buttonBackgroundColor: "brand1",
                buttonTextColor: "primary",
                buttonFontSize: "sizeSecondary",
                buttonBorderRadius: 10,
            },
        },

        plateModal: {
            cssPrefix: "plate-modal",
            styles: {
                backgroundColor: "primary",
                borderRadius: "radius.md",
                padding: "space.sm",
                shadow: "shadow.lg",
                titleColor: "secondary",
                titleFontSize: "sizeSecondary",
                descriptionColor: "secondary",
                descriptionFontSize: "sizeTertiary",
                priceColor: "brand2",
                priceFontSize: "sizeSecondary",
                primaryButtonBackgroundColor: "brand1",
                primaryButtonTextColor: "primary",
                primaryButtonFontSize: "sizeSecondary",
                primaryButtonBorderRadius: 10,
                secondaryButtonBackgroundColor: "primary",
                secondaryButtonTextColor: "secondary",
                secondaryButtonFontSize: "sizeSecondary",
                secondaryButtonBorderRadius: 10,
            },
        },

        cartSummary: {
            cssPrefix: "cart-summary",
            styles: {
                backgroundColor: "primary",
                borderRadius: "radius.md",
                padding: "space.sm",
                shadow: "shadow.sm",
                labelColor: "secondary",
                labelFontSize: "sizeSecondary",
                priceBackgroundColor: "brand1",
                priceTextColor: "primary",
                priceFontSize: "sizePrimary",
                priceBorderRadius: 10,
                pricePadding: 8,
                buttonBackgroundColor: "brand2",
                buttonTextColor: "primary",
                buttonFontSize: "sizeSecondary",
                buttonBorderRadius: 10,
            },
        },

        cartModal: {
            cssPrefix: "cart-modal",
            styles: {
                backgroundColor: "primary",
                borderRadius: "radius.md",
                padding: "space.sm",
                titleColor: "secondary",
                itemColor: "secondary",
                fontSize: "sizeSecondary",
                whatsappButtonBackgroundColor: "secondary",
                whatsappButtonTextColor: "primary",
                whatsappButtonBorderRadius: 10,
                closeButtonColor: "secondary",
            },
        },
    },

    // sigue igual (runtime)
    i18n: {
        defaultLang: "es",
        availableLangs: ["es", "en"],
        translations: {
            es: { menu_title: "Menú", welcome: "Bienvenido" },
            en: { menu_title: "Menu", welcome: "Welcome" },
        },
    },

    cart: {
        visibility: { "*": true },
        schedule: { enabled: false, ranges: [] },
    },
};
